import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  collection,
  query,
  orderBy,
  getDocs,
  addDoc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../services/firebase";
import { Tag } from "../types";

/**
 * Firestore からタグ一覧を取得
 */
const fetchTags = async (): Promise<Tag[]> => {
  const tagsQuery = query(collection(db, "tags"), orderBy("name", "asc"));
  const snapshot = await getDocs(tagsQuery);

  return snapshot.docs.map((docSnap) => {
    const data = docSnap.data();
    const createdAt = data.createdAt as Timestamp | null;

    return {
      id: docSnap.id,
      name: data.name,
      createdAt: createdAt?.toDate?.().toISOString() || new Date().toISOString(),
    };
  });
};

/**
 * Firestore にタグを追加
 */
const addTag = async (name: string): Promise<Tag> => {
  const docRef = await addDoc(collection(db, "tags"), {
    name,
    createdAt: serverTimestamp(),
  });

  return {
    id: docRef.id,
    name,
    createdAt: new Date().toISOString(),
  };
};

/**
 * タグ一覧取得用カスタムフック
 */
export const useTags = () => {
  return useQuery(["tags"], fetchTags, {
    staleTime: 600000, // 10分間はキャッシュを新鮮とみなす
  });
};

/**
 * タグ追加用カスタムフック
 */
export const useAddTag = () => {
  const queryClient = useQueryClient();

  return useMutation(addTag, {
    onSuccess: (newTag) => {
      // 追加後にキャッシュを更新
      queryClient.setQueryData<Tag[]>(["tags"], (oldTags) => {
        if (!oldTags) return [newTag];
        // 名前順でソートして挿入
        const updatedTags = [...oldTags, newTag];
        return updatedTags.sort((a, b) => a.name.localeCompare(b.name));
      });
    },
  });
};
